import React, { useEffect, useRef, useState } from 'react';

/**
 * Animated counter. Parses the target value ("25+", "450", "98%", "24/7")
 * and counts up from 0 to the numeric portion. Non-numeric values
 * just fade in after the start delay.
 */
const parseValue = (val) => {
  if (!val) return { numeric: false, raw: '' };
  const match = String(val).match(/^([^\d]*)(\d+)(.*)$/);
  if (!match) return { numeric: false, raw: String(val) };
  const [, prefix, num, suffix] = match;
  // Reject non-numeric composites like "24/7" (contains slash + digit)
  if (suffix && /\d/.test(suffix)) return { numeric: false, raw: String(val) };
  return { numeric: true, prefix: prefix || '', target: parseInt(num, 10), suffix: suffix || '' };
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const CountUp = ({
  value,
  start = false,
  duration = 1600,
  startDelay = 0,
  className = '',
}) => {
  const parsed = parseValue(value);
  const [display, setDisplay] = useState(parsed.numeric ? `${parsed.prefix}0${parsed.suffix}` : '');
  const [shown, setShown] = useState(false);
  const rafRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!start) return;
    if (!parsed.numeric) {
      timerRef.current = setTimeout(() => setShown(true), startDelay);
      return () => clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        const v = Math.floor(easeOutCubic(p) * parsed.target);
        setDisplay(`${parsed.prefix}${v}${parsed.suffix}`);
        if (p < 1) rafRef.current = requestAnimationFrame(step);
        else setDisplay(`${parsed.prefix}${parsed.target}${parsed.suffix}`);
      };
      rafRef.current = requestAnimationFrame(step);
    }, startDelay);
    return () => {
      clearTimeout(timerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [start]);

  if (!parsed.numeric) {
    return (
      <span
        className={className}
        style={{
          display: 'inline-block',
          opacity: shown ? 1 : 0,
          transform: shown ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 900ms cubic-bezier(0.22,1,0.36,1), transform 900ms cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        {parsed.raw}
      </span>
    );
  }

  return <span className={className}>{display}</span>;
};

export default CountUp;
